/**
 * Logos section component for Stack Forward website
 */

import React from 'react';

/**
 * Logo interface
 */
interface Logo {
  name: string;
  description: string;
  placeholder: string;
}

/**
 * Logos section component
 */
export const Logos: React.FC = () => {
  // Technology logos data (8 logos for consistent 2x4 grid)
  const logos: Logo[] = [
    {
      name: 'ERPNext',
      description: 'Full-featured open-source ERP system',
      placeholder: 'erpnext.svg',
    },
    {
      name: 'Odoo',
      description: 'All-in-one business management suite',
      placeholder: 'odoo.svg',
    },
    {
      name: 'Frappe HR',
      description: 'Modern HRMS for employee management',
      placeholder: 'frappe.svg',
    },
    {
      name: 'Nextcloud',
      description: 'Self-hosted collaboration platform',
      placeholder: 'nextcloud.svg',
    },
    {
      name: 'GitLab CE',
      description: 'Complete DevSecOps lifecycle platform',
      placeholder: 'gitlab.svg',
    },
    {
      name: 'Mattermost',
      description: 'Secure enterprise team collaboration',
      placeholder: 'mattermost.svg',
    },
    {
      name: 'Keycloak',
      description: 'Identity and access management (IAM)',
      placeholder: 'keycloak.svg',
    },
    {
      name: 'Vault',
      description: 'Secrets and encryption key management',
      placeholder: 'vault.svg',
    },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Trusted Open-Source Technologies
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            We specialize in implementing and supporting industry-leading
            open-source solutions that power modern businesses of all sizes.
          </p>
        </div>

        {/* Logos grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {logos.map((logo) => (
            <div
              key={logo.name}
              className="group flex flex-col items-center p-6 rounded-xl border border-neutral-200 hover:border-primary-300 hover:shadow-md transition-all duration-300 bg-white"
            >
              {/* Logo */}
              <div className="w-16 h-16 mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <img
                  src={`/logos/${logo.placeholder}`}
                  alt={`${logo.name} logo`}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Logo name */}
              <h3 className="text-lg font-semibold text-neutral-900 mb-2 text-center">
                {logo.name}
              </h3>

              {/* Description */}
              <p className="text-sm text-neutral-600 text-center leading-relaxed">
                {logo.description}
              </p>

              {/* Hover effect */}
              <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-8 h-1 bg-primary-500 rounded-full mx-auto"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-neutral-600 mb-6">
            Don't see your preferred technology? We work with many more
            open-source solutions.
          </p>
          <button
            onClick={() => {
              const contactSection = document.querySelector('#contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="btn-secondary"
          >
            Discuss Your Stack
          </button>
        </div>
      </div>
    </section>
  );
};
